import React, { useMemo, useState } from "react";
import { MiBox, MiList, MiMenu, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { ICityResponse } from "@miplatform/types";
import { formatDate, isRecentlyAdded } from "@miplatform/utils";
import { mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import { AddButton } from "@mibuildweb/components";
import { usePermissions, useCities } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";

export function ConfigCitiesList() {
  const { navigate } = useRouter();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [selectedCity, setSelectedCity] = useState<ICityResponse | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const {
    deleteCity,
    getCities,
    cities,
    setCities,
    recentCities,
    setRecentCities,
    getRecentCities,
    isLoading,
  } = useCities();

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.CITY.CREATE);
  const canEdit = hasPermission(PERMISSIONS.CITY.UPDATE);
  const canDelete = hasPermission(PERMISSIONS.CITY.DELETE);

  const BASE_PATH = CONFIG_PATHS[CONFIG_PAGES.CITIES];

  const refreshCities = useMemo(
    () => ({ triggerRefresh }),
    [triggerRefresh]
  );

  const refreshData = () => setTriggerRefresh((prev) => !prev);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleAdd = () => {
    navigate(`${BASE_PATH}/add-city`);
  };

  const handleEdit = (item: ICityResponse) => {
    navigate(`${BASE_PATH}/${item.id}`);
  };

  const handleOpenDeleteModal = (item: ICityResponse) => {
    setSelectedCity(item);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedCity(null);
  };

  const handleDelete = async () => {
    if (selectedCity) {
      await deleteCity({
        id: selectedCity.id,
        modifiedOn: selectedCity.modifiedOn,
        name: selectedCity.cityName,
      });
    }
    handleCloseDeleteModal();
    refreshData();
  };


  return (
    <ConfigPageLayout
      title="Cities"
      search={search}
      onHandleSearch={handleSearchChange}
      hideOnBack
      loading={isLoading}
      titleActionComponent={
        canCreate && (
          <AddButton onClick={handleAdd} label="Add City" />
        )
      }
    >
      <MiBox>
        <MiList
          viewType="card"
          dataProvider={getCities}
          loading={isLoading}
          data={cities ?? []}
          setData={setCities}
          recentData={recentCities}
          setRecentData={setRecentCities}
          recentDataProvider={getRecentCities}
          CardComponent={CityCard}
          cardProps={{
            canEdit,
            canDelete,
            onEdit: handleEdit,
            onDelete: handleOpenDeleteModal,
            onClick: handleEdit,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          refresh={refreshCities}
          emptyListProps={{
            text: "No Cities found",
          }}
        />
      </MiBox>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        description={`You are about to delete ${selectedCity?.cityName}. Are you sure you would like to continue?`}
        textPositive={BUTTON_LABELS.DELETE}
        handlePositive={handleDelete}
        handleNegative={handleCloseDeleteModal}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
}

function CityCard({
  item,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onClick,
}: Readonly<{
  item: ICityResponse;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (item: ICityResponse) => void;
  onDelete: (item: ICityResponse) => void;
  onClick?: (item: ICityResponse) => void;
}>) {
  const theme = useTheme();
  const isNew = isRecentlyAdded(item.createdOn);

  const menuActions = [];
  if (canEdit) {
    menuActions.push({
      label: BUTTON_LABELS.EDIT,
      action: () => onEdit(item),
      icon: mdiPencilOutline,
    });
  }
  if (canDelete) {
    menuActions.push({
      label: BUTTON_LABELS.DELETE,
      action: () => onDelete(item),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
    });
  }

  return (
    <Box
      onClick={() => onClick?.(item)}
      sx={{
        backgroundColor: "background.paper",
        padding: 2,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.grey.light },
        gap: 2,
      }}
    >
      <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="HD2" color="primary">
          {item.cityName}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {isNew && (
            <Box
              sx={{
                backgroundColor: theme.palette.warning.light,
                border: `1px solid ${theme.palette.warning.main}`,
                color: theme.palette.warning.main,
                borderRadius: 1,
                px: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="BD1">New</Typography>
            </Box>
          )}
          {item.modifiedOn && (
            <>
              <Typography variant="HD1">Last Updated:</Typography>
              <Typography variant="HD1" fontWeight={400}>
                {formatDate(item.modifiedOn, "MMMM DD, YYYY")}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="BD1" color="text.secondary">
          County
        </Typography>
        <Typography variant="BD2">{item.countyName}</Typography>
      </Box>

      <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="BD1" color="text.secondary">
          Inspection Authorities
        </Typography>
        <Typography variant="BD2">
          {item.inspectionAuthorityCount ?? 0}
        </Typography>
      </Box>

      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={menuActions} />
      </Box>
    </Box>
  );
}